<template>
	<div class="points-window">
		<!-- Показать блок ПЛ если пользователь имеет возможность списать или получить баллы -->
		<template v-if="user && user.loyalty_id && loyaltyProfile.phone_verified_at">
			<charge-points
				:value="loyaltyData"
				:price="finalPrice"
				:awarded-points="awardedPoints"
				:loyalty-profile="loyaltyProfile"
				:card-background="cardBackground"
				:selected-products="calculatedSelectedProducts"
				:all-products-from-group="allProductsFromGroup"
				:is-allowed-active-points-charged="isBlockContainsLoyaltyChargeProduct"
				:is-allowed-to-earn="isBlockContainsLoyaltyEarnProduct"
				:is-load-points="isAwardedPointsLoading"
				:is-promo-code-used="isPromoCodeUsed"
				:charged-points="+chargedPoints"
				:charged-premium-points="+chargedPremiumPoints"
				:has-students="hasStudents"
				:current-product="currentProduct"
				@input="onInput"
				@updatePoints="$emit('updatePoints')"
				@hidePoints="$emit('hidePoints')"
			/>
		</template>

		<!-- Показать блок ПЛ если у пользователя не верифицирован номер телефона -->
		<div v-if="user && user.loyalty_id && !loyaltyProfile.phone_verified_at" class="card">
			<div
				v-if="loyaltyProfile.level"
				class="card-header"
				:style="{
					backgroundColor: cardBackground,
				}"
			>
				<h3 class="card-title">
					<span class="font-weight-bolder">{{ loyaltyProfile.level.name }}</span> уровень
				</h3>
				<p v-if="isBlockContainsLoyaltyChargeProduct && premiumPointsAbsent" class="mb-0">
					Вы можете оплатить баллами до
					{{ Math.round(loyaltyProfile.level.max_payment_percent) }}%
					стоимости
				</p>
			</div>
			<div class="card-body">
				<template v-if="isBlockContainsLoyaltyChargeProduct">
					Для списания баллов необходимо подтвердить номер телефона в
					<nuxt-link to="/user/profile/loyalty/card/">
						Личном кабинете.
					</nuxt-link>
				</template>
				<div
					v-if="awardedPoints > 0 && isBlockContainsLoyaltyEarnProduct"
					class="points-table"
				>
					Вам будет начислено {{ awardedPoints }} баллов.
				</div>
			</div>
		</div>

		<!-- Показать пригласительный блок ПЛ если пользователь не является участником ПЛ -->
		<template v-else-if="showDefaultBlock">
			<div v-if="!user || (user && !user.loyalty_id) && isBlockContainsLoyaltyEarnProduct">
				<loyalty-default-block :original-price="originalPrice" />
			</div>
		</template>

		<!-- Показать итоговую стоимость с учётом списываемых баллов ПЛ -->
		<div v-if="!hideFinalPrice" class="amount_block mt-3 mb-3">
			<div
				class="d-flex justify-content-between align-items-center"
			>
				<span class="title">Итого</span>
				<span
					v-if="loyaltyData && hasChargedPoints && isBlockContainsLoyaltyChargeProduct"
					class="title"
				>
					{{ createMaskAmount(finalPriceWithLoyaltyCharge) }} руб
				</span>
				<!-- <span v-else-if="isPromoCodeUsed || discount || isActivatePromocode" class="title">
					{{ createMaskAmount(finalPrice) }} руб
				</span> -->
				<span v-else class="title">
					{{ createMaskAmount(finalPrice) }} руб
				</span>
			</div>
			<div
				v-if="isPromoCodeUsed || (discountPrice > 0 && discount) || isActivatePromocode"
				class="d-flex justify-content-end align-items-center"
			>
				<span class="original_amount">
					{{ createMaskAmount(originalPrice) }} руб
				</span>
			</div>
			<!-- <div v-if="discount" class="d-flex justify-content-end align-items-center">
						{{ $t('offerEnds') }} {{
							item.sellable_program.active_to | moment("DD MMMM")
						}}
					</div> -->
		</div>
	</div>
</template>

<script>
import formulateFormScenario from '~/mixins/formulateFormScenario';
import { LOYALTY_LEVELS } from '~/utils/dictionaries';
import LoyaltyDefaultBlock from '~/components/loyalty/LoyaltyDefaultBlock.vue';
import ChargePoints from './ChargePoints.vue';

export default {
	components: {
		ChargePoints,
		LoyaltyDefaultBlock,
	},
	mixins: [formulateFormScenario],
	props: {
		loyaltyData: { type: Object, default: () => ({}) },
		finalPrice: { type: Number, default: 0 },
		originalPrice: { type: Number, default: 0 },
		chargedPoints: { type: Number, default: 0 },
		chargedPremiumPoints: { type: Number, default: 0 },
		isPromoCodeUsed: { type: Boolean, default: false },
		showDefaultBlock: { type: Boolean, default: false },
		hideFinalPrice: { type: Boolean, default: false },
		products: { type: Array, default: () => [] },
		hasStudents: { type: Boolean, default: false },
		currentProduct: { type: Object, default: () => ({}) },
	},
	data() {
		return {
			levels: LOYALTY_LEVELS,
			loyaltyProfile: {},
			cardBackground: null,
		};
	},
	computed: {
		user() {
			return this.$auth.user;
		},
		finalPriceWithLoyaltyCharge() {
			return this.loyaltyData?.actualAmount;
		},
		hasChargedPoints() {
			return this.loyaltyData.needChargePoints || this.loyaltyData.needChargePremiumPoints;
		},
		premiumPointsAbsent() {
			return Number(this.loyaltyProfile.premium_points) <= 0;
		},
		calculatedSelectedProducts() {
			const products = this.products && !this.selectedProducts.length
				? this.products
				: this.selectedProducts;

			return this.getProductsWithActualPrice(products);
		},
	},
	watch: {
		'$auth.user': {
			async handler(user) {
				if (user) {
					await this.getLoyaltyProfile();
				}
			},
			immediate: true,
		},
		currentProduct(value) {
			this.selectedProducts = [value];
		},
	},
	methods: {
		async getLoyaltyProfile() {
			const { data } = await this.$api.getLoyaltyProfile();

			if (data) {
				this.loyaltyProfile = data;
				const [currentLevel] = this.levels.filter((el) => el.id === this.loyaltyProfile.level.id);
				this.cardBackground = currentLevel.color;
			}
		},
		onInput(val) {
			this.$emit('setLoyaltyData', val);
		},
	},
};
</script>

<style lang="scss" scoped>
@import "assets/styles/sk_variables";
// For use bootstrap vars and mixins inside
@import "assets/styles/bootstrap/scss/functions";
@import "assets/styles/bootstrap/scss/variables";
@import "assets/styles/bootstrap/scss/mixins";

.title {
	margin-bottom: 0;
}

.card-header {
	background-color: #E7E0D0;
}

.card-title {
	font-size: 1.5rem;
}

.amount_block {
	// height: 3rem;

	.original_amount {
		color: $border-color;
		font-weight: bold;
		text-decoration: line-through;
		font-size: 1.25rem;
	}
}

.points-table {
	border: 1px solid $blue;
	padding: 0.5rem 1rem;
	margin-top: 0.5rem;
	border-radius: 0.7rem;
	max-width: 13rem;
}

.points-window__charge-points-skeleton {
	height: 130px;
	width: 100%;
	background: linear-gradient(to right, #f4f4f4, #dedede);
	background: rgba(128, 128, 128, 0.5);
	pointer-events: none;
	background: linear-gradient(
			270deg,
			rgba(169, 169, 169, 0.2),
			rgba(128, 128, 128, 0.5),
			rgba(169, 169, 169, 0.2)
	);
	background-size: 200% 200%;
	animation: points-window__charge-points-skeleton 3s linear infinite;
}

@keyframes points-window__charge-points-skeleton {
	0% { background-position: -100% 0; }
	100% { background-position: 200% 0; }
}

</style>
